'use client';

import { FormEventHandler } from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';
import styled from '@emotion/styled';
import Button from '../../../../components/common/button/Button';
import { FlightSearchCombobox } from './FlightSearchCombobox';
import { useFlightStores } from '../../shared/context/flightStoresContext';
import { getFlightsListUrl } from '../../use-cases/getFlightsListUrl';
import { IFlightSearchSuggestion } from '../domain/models/IFlightSearchSuggestion';
import { FlightListScope } from '../../overview/domain/constants/FlightListScope';
import { ArrowForwardIcon, Css, Responsive } from '../../../../ui';
import { AirportCodes } from '../../../../types/airports';
import { fadeIn } from '../../../../styles/animation/fadeIn';

export const FlightSearchForm = styled.form<{ isFullWidth: boolean }>`
	display: flex;
	flex-direction: ${(props) => (props.isFullWidth ? `column` : `row`)};
	gap: ${Css.pxToRem(16)};
	border: none;
	padding: 0;
	margin: 0;
	align-items: stretch;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		flex-direction: column;
		gap: ${Css.pxToRem(18)};
		align-items: flex-end;
	}

	${fadeIn}
`;

const StyledButton = styled(Button)`
	height: ${Css.pxToRem(45)};
	white-space: nowrap;
	justify-content: center;
	width: fit-content;
	align-self: end;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		height: ${Css.pxToRem(48)};
		width: 100%;
	}
`;

interface IProps {
	submitOnClear?: boolean;
	scope?: FlightListScope;
	airport: AirportCodes;
	isFullWidth?: boolean;
}

export const FlightSearch = observer(
	({ submitOnClear = true, scope, airport, isFullWidth }: IProps) => {
		const router = useRouter();
		const { flightSuggestionStore, flightListStore } = useFlightStores();

		const onSubmit: FormEventHandler = (event) => {
			event.preventDefault();
			const { query } = flightSuggestionStore;

			submitQuery(query);
		};

		const onInputClear = () => {
			flightSuggestionStore.setQuery('');
			flightListStore.setQuery('');

			if (submitOnClear) {
				submitQuery('');
			}
		};

		const onItemClick = (item: IFlightSearchSuggestion) => {
			submitQuery(item.stringValue, false);
		};

		const onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				const { query } = flightSuggestionStore;
				submitQuery(query, false);
			}
		};

		const submitQuery = (query: string, update = true) => {
			const url = getFlightsListUrl(airport, scope, query);
			if (update) {
				router.push(url, { scroll: false });
			}
		};

		return (
			<FlightSearchForm isFullWidth={isFullWidth ?? false} onSubmit={onSubmit}>
				<FlightSearchCombobox
					value={flightSuggestionStore.query}
					onClear={onInputClear}
					onItemClick={onItemClick}
					onKeyUp={onKeyUp}
				/>
				<StyledButton
					type="submit"
					icon={<ArrowForwardIcon aria-label="Find flight" />}
				>
					Find flight
				</StyledButton>
			</FlightSearchForm>
		);
	},
);
